import React, { useEffect } from "react";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import {getObjectByPath, uniqueId} from "../../../utils";
import { variableCurrencyValue } from "../../../components/field-table";
import { CreativeStudio, CreativeStudioType } from "../../../generated/graphql";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import {CreativeStudioTypeSelect} from "../../../components/MaterialSelects/CreativeStudioTypeSelect";
import {CreativeStudioTypeDetailSelect} from "../../../components/MaterialSelects/CreativeStudioTypeDetailSelect";
import {IsoDatePicker} from "../../../components/IsoDatePicker";
import { RegionSelectGrouped } from "../../../components/MaterialSelects/RegionSelectGrouped";


function newRow() {
    return {
        campaignComponent: {
            unit_value_local: 0,
            qty: '',
        },
        notes: "",
        start_date: null,
        end_date: null,
        creativeStudioType: null,
        creativeStudioTypeDetail: null,
    };
}

const summaries = {
    footer: (old, newRow) => {
        return !newRow;
    },
};

const CreativeStudioPane = () => {
    const {
        values,
        setValue,
        loading,
        errors,
        readOnly,
        setTab,
        isReadOnly
    } = useOutletContext<CampaignOutletContextValue>();
    const columns: FieldTableColumnProps<CreativeStudio>[] = [
        {
            header: 'Region',
            accessor: 'campaignComponent.region',
            sort: 'campaignComponent.region.name',
            dataType:'select',
            width:'minmax(14rem, max-content)',
            options: {
                renderViewer:({field,row})=>{
                    const rt = getObjectByPath(row, 'campaignComponent.regionType');
                    return rt?.name ? rt.name + ' / ' + field?.value?.name : '';
                },
                renderEditor: function rtEdit({field}) {
                    const changeRt = (evt) => {
                        const name = evt.target.name;
                        field.onChange({target: {name: name, value: evt?.target?.value}});
                        field.onChange({target: {name: name + 'Type', value: evt?.target?.value?.regionType}});
                    }
                    return <RegionSelectGrouped isCompositeChange={true} {...field} allowBlank={true} onChange={changeRt}
                                                autoFocus={true}/>;
                }
            }
        },
        {
            header: 'Type',
            accessor: 'creativeStudioType',
            sort: 'creativeStudioType.name',
            dataType: 'select',
            options: {
                label: 'name',
                renderEditor: ({ field }) =>
                    (<CreativeStudioTypeSelect {...field} autoFocus={true} />)
            }
        }, {
            header: 'Details',
            accessor: 'creativeStudioTypeDetail',
            sort: 'creativeStudioTypeDetail.name',
            dataType: 'select',
            options: {
                label: 'name',
                renderEditor: ({ field, row }) => {
                    const rt = getObjectByPath(row, 'creativeStudioType');
                    return <CreativeStudioTypeDetailSelect creativeStudioType={rt ? rt.id : null} {...field} autoFocus={true} />;
                }
            }
        }, {
            header: 'Notes',
            accessor: 'notes',
            dataType: 'text',
        }, {
            header: 'Start Date',
            accessor: 'start_date',
            dataType: 'date',
            options: {
                renderEditor: ({ field }) => (<IsoDatePicker autoFocus={true} {...field} />)
            }
        },
        {
            header: 'End Date',
            accessor: 'end_date',
            className: 'column-date',
            dataType: 'date',
            options: {
                renderEditor: ({ field }) => <IsoDatePicker autoFocus={true} {...field} />
            }
        }, {
            header: 'Unit Cost',
            accessor: 'campaignComponent.unit_value_local',
            currencyAccessor: 'campaignComponent.currency',
            dataType: 'currency',
            readOnly: () => { return isReadOnly(true) },
            options: {
                currency: {
                    allowContextCurrency: true,
                    accessor: 'campaignComponent.currency'
                }
            },
            summaries: {
                // eslint-disable-next-line react/display-name
                footer: () => {
                    return <b style={{ display: 'flex', alignItems: 'center' }}>Total</b>
                }
            }

        }, {
            header: 'Qty',
            accessor: 'campaignComponent.qty',
            dataType: 'number',
            readOnly: () => { return isReadOnly(true) },
            summaries: {
                footer: ({ rows }) => {
                    return rows.reduce((total, current) => { return total + (parseInt(current.campaignComponent?.qty) || 0); }, 0)
                }
            }
        }, {
            header: 'Total',
            accessor: 'total',
            dataType: 'currency',
            readOnly: true,
            options: {
                getValue: ({ row }) => {
                    return row.campaignComponent?.qty * row.campaignComponent?.unit_value_local;
                },
                currency: {
                    accessor: 'campaignComponent.currency'
                }
            },
            summaries: {
                footer: ({ rows, currency }) => {
                    const defaultCurrency = currency?.id;
                    let seenOther = false;
                    const value = rows.reduce((total, current) => {
                        const cid = current.campaignComponent?.currency?.id;
                        if (cid && defaultCurrency != cid) {
                            seenOther = true;
                        }
                        return (total + (parseInt(current.campaignComponent?.qty) * parseInt(current.campaignComponent?.unit_value_local)));
                    }, 0)
                    if (seenOther) return null;
                    return variableCurrencyValue({ currency: currency, value: value });
                }
            }
        },
    ];

    useEffect(() => {
        setTab("creative-studio");
    }, []);
    const allowAddDelete = !(readOnly || isReadOnly(true));

    return (
        <FieldTable
            newRow={newRow}
            newRowLabel={allowAddDelete ? "Add Creative Studio" : null}
            columns={columns}
            values={values.creativeStudio}
            setValue={setValue}
            loading={loading}
            errors={errors}
            hasDelete={allowAddDelete}
            name="creativeStudio"
            className="campaigns__creative-studio"
            summaries={summaries}
            readOnly={readOnly}
        />
    );
};

export default CreativeStudioPane;
